import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import CloseIcon from '@mui/icons-material/Close';
import { safeAreaMaxHeight, safeAreaModalBackdropPadding } from 'utils/safeArea';
import { isWithinTapSlop, tapOrigin, TapOrigin } from 'utils/tapGesture';
import { primaryBtnStyle, secondaryBtnStyle } from 'components/GameModal/GameModal';
import SupportLanding from './SupportLanding';
import SupportChat from './SupportChat';
import SupportSuccess from './SupportSuccess';
import { Ticket } from './useSupportApi';

type SupportPanelProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Is there half-typed work in the panel that closing would throw away?
 *
 * The drafts live in the CHILDREN — `SupportChat`'s reply box, `SupportLanding`'s
 * new-ticket subject and message — and none of it is lifted to this component,
 * so there is no state here to read. Rather than prop-drill a `hasDraft` flag
 * through three components (and have to remember it for the next sub-view added),
 * ask the rendered subtree: every one of those fields is a CONTROLLED input, so
 * its DOM value IS the draft state, and any future field is covered for free.
 *
 * Deliberately only `textarea` and text `input`s — the category `<select>` is a
 * pre-filled choice rather than typing, and warning about it would be a nag.
 */
const hasUnsentDraft = (panel: HTMLElement | null): boolean => {
  if (!panel) return false;
  const fields = panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'textarea, input[type="text"], input:not([type])'
  );
  return Array.from(fields).some((field) => field.value.trim() !== '');
};

const SupportPanel = ({ open, onClose }: SupportPanelProps) => {
  const [activeTicket, setActiveTicket] = useState<Ticket | null>(null);
  const [createdTicket, setCreatedTicket] = useState<Ticket | null>(null);
  // Raised when an outside tap would discard a half-typed reply — see the
  // scrim handlers below.
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!backdropRef.current || !panelRef.current) return;

    if (open) {
      gsap.to(backdropRef.current, { opacity: 1, duration: 0.22, ease: 'power2.out' });
      gsap.fromTo(
        panelRef.current,
        { y: 24, opacity: 0, scale: 0.97 },
        { y: 0, opacity: 1, scale: 1, duration: 0.28, ease: 'power3.out' }
      );
    }
  }, [open]);

  const handleClose = () => {
    if (!backdropRef.current || !panelRef.current) {
      onClose();
      return;
    }
    gsap.to(panelRef.current, { y: 16, opacity: 0, scale: 0.97, duration: 0.18, ease: 'power2.in' });
    gsap.to(backdropRef.current, {
      opacity: 0,
      duration: 0.18,
      ease: 'power2.in',
      onComplete: () => {
        setActiveTicket(null);
        setCreatedTicket(null);
        setConfirmDiscard(false);
        onClose();
      },
    });
  };

  /**
   * Every way the PLAYER asks to close the panel funnels through here.
   *
   * The draft warning is a property of closing, not of the gesture that asked
   * for it: an outside tap and the header ✕ discard exactly the same half-typed
   * reply, so guarding only the tap meant the ✕ was a silent, one-click version
   * of the data loss we had just decided was worth warning about (EN-10789).
   * `handleClose` stays the unguarded close — the confirm dialog's own Discard
   * button calls it directly, having already asked.
   */
  const requestClose = () => {
    // Warn only when there IS something to lose; warning on an untouched panel
    // is a nag.
    if (hasUnsentDraft(panelRef.current)) {
      setConfirmDiscard(true);
      return;
    }
    handleClose();
  };

  // EN-10789: the panel dismisses on an outside tap like everything else. Same
  // gesture rule as GameModal's scrim — pointer DOWN and UP both on the scrim
  // itself, within tap slop — so a drag out of the panel (selecting the text of
  // a reply) or a scroll that merely ends out there cannot close it.
  const scrimGestureRef = useRef<{ pointerId: number; origin: TapOrigin } | null>(null);

  const isScrimItself = (event: React.SyntheticEvent<HTMLDivElement>) =>
    event.target === event.currentTarget;

  const handleScrimPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.isPrimary === false) {
      scrimGestureRef.current = null;
      return;
    }
    scrimGestureRef.current = isScrimItself(event)
      ? { pointerId: event.pointerId, origin: tapOrigin(event) }
      : null;
  };

  const handleScrimPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = scrimGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    scrimGestureRef.current = null;

    if (!isScrimItself(event)) return;
    if (event.button > 0) return;
    if (!isWithinTapSlop(gesture.origin, event)) return;

    // An unsent reply is real work — see requestClose, which is also what the
    // header ✕ goes through so the two cannot drift apart again.
    requestClose();
  };

  if (!open) return null;

  return (
    // The backdrop dismisses on a deliberate outside tap (EN-10789). The
    // in-progress-reply risk that used to justify no handler at all is now
    // handled where it belongs — by asking, rather than by making the panel
    // the one surface on the table that ignores a tap outside it.
    <div
      ref={backdropRef}
      data-testid='support-backdrop'
      style={backdropStyle}
      onPointerDown={handleScrimPointerDown}
      onPointerUp={handleScrimPointerUp}>
      <div ref={panelRef} style={panelStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <span style={titleStyle}>Support</span>
          {/* requestClose, not handleClose: the ✕ throws away the same unsent
              reply an outside tap does, so it asks the same question. */}
          <button style={closeBtnStyle} onClick={requestClose} aria-label='Close support'>
            <CloseIcon style={{ fontSize: '0.85rem' }} />
          </button>
        </div>

        {/* Amber dashed divider */}
        <div style={dividerStyle} />

        {/* Content */}
        <div style={contentStyle}>
          {createdTicket ? (
            <SupportSuccess
              ticket={createdTicket}
              onBackToLobby={handleClose}
            />
          ) : activeTicket ? (
            <SupportChat
              ticket={activeTicket}
              onBack={() => setActiveTicket(null)}
            />
          ) : (
            <SupportLanding
              onOpenTicket={setActiveTicket}
              onCreated={setCreatedTicket}
            />
          )}
        </div>
      </div>

      {/* Discard warning. Rendered INSIDE this backdrop rather than as a
          GameModal: GameModal portals to <body> at zIndex 200 and this panel's
          own backdrop is 260, so a GameModal would be painted behind it. */}
      {confirmDiscard && (
        <div style={confirmOverlayStyle} data-testid='support-discard-confirm'>
          <div style={confirmCardStyle}>
            <div style={confirmTitleStyle}>Discard your message?</div>
            <div style={confirmBodyStyle}>
              You have typed a message that has not been sent yet. Closing Support will lose it.
            </div>
            <div style={confirmBtnRowStyle}>
              <button style={secondaryBtnStyle} onClick={() => setConfirmDiscard(false)}>
                Keep writing
              </button>
              <button
                style={primaryBtnStyle}
                onClick={() => {
                  setConfirmDiscard(false);
                  handleClose();
                }}>
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPanel;

const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0,0,0,0.65)',
  backdropFilter: 'blur(5px)',
  zIndex: 260,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Pad by the device safe-area insets (plus the base 1rem) so the centered
  // panel never sits under the notch or home indicator on mobile.
  ...safeAreaModalBackdropPadding(),
  // Re-enable clicks: the LobbyOverlayLock wrapper rotates this to portrait via
  // a `transparentToPointer` ForcePortrait (rotated layer = pointer-events:none),
  // and pointer-events is inherited — so without this the panel and its close
  // button aren't clickable on mobile-landscape. Mirrors CashierPanel.
  pointerEvents: 'auto',
  opacity: 0,
};

const panelStyle: CSSProperties = {
  width: '100%',
  maxWidth: '440px',
  // Subtract the vertical safe-area insets so a full-height panel's scrollable
  // content clears the notch / home indicator on mobile.
  maxHeight: safeAreaMaxHeight('82vh'),
  background: 'linear-gradient(180deg, #2b1a08 0%, #1c2e4a 28%, #0b1424 55%, #060c18 100%)',
  borderRadius: '14px',
  border: '1px solid rgba(212,168,83,0.28)',
  boxShadow: '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(212,168,83,0.08)',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'Outfit', sans-serif",
  color: 'rgba(255,255,255,0.9)',
  overflow: 'hidden',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.8rem 1rem',
  flexShrink: 0,
};

const titleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#fff',
  letterSpacing: '0.01em',
};

const closeBtnStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.1)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: '50%',
  color: 'rgba(255,255,255,0.7)',
  width: '28px',
  height: '28px',
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.12s ease',
  flexShrink: 0,
};

const dividerStyle: CSSProperties = {
  height: '2px',
  flexShrink: 0,
  // Match the orange dashed divider used in Account Profile / Cashier headers
  // (9px dashes, 6px gaps, saloonTokens.orange500 at 70%).
  background:
    'repeating-linear-gradient(to right, rgba(232,130,31,0.7) 0 9px, transparent 9px 15px)',
};

const contentStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: 'none',
};

// Sits over the panel inside the same backdrop — see the note at its usage.
const confirmOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
  zIndex: 1
};

const confirmCardStyle: CSSProperties = {
  width: '100%',
  maxWidth: '340px',
  background: 'rgba(20, 30, 42, 0.98)',
  border: '1px solid rgba(212,168,83,0.28)',
  borderRadius: '14px',
  boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
  padding: '1.1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem',
  fontFamily: "'Outfit', sans-serif"
};

const confirmTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#fff',
  textAlign: 'center'
};

const confirmBodyStyle: CSSProperties = {
  fontSize: '0.85rem',
  lineHeight: 1.5,
  color: 'rgba(255,255,255,0.6)',
  textAlign: 'center'
};

const confirmBtnRowStyle: CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  width: '100%',
  marginTop: '0.35rem'
};
